import { Flex, Button } from "@radix-ui/themes";
import { useMutation } from "react-query";
import { useNoticeApi } from "@/v2/entities/notice/Notice.api";
import { useCallback } from "react";
import { toast } from "sonner";
import { Dialog } from "@/components/shared/dialog";
import { NoticeForm, NoticeFormPayload } from "./NoticeForm";
import { FormProvider, useForm } from "react-hook-form";

interface CreateNoticeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (noticeId: string) => void;
}

export function CreateNoticeDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateNoticeDialogProps) {
  const { createNotice } = useNoticeApi();

  const methods = useForm<NoticeFormPayload>({
    mode: "onChange",
    defaultValues: { name: "", version: "" },
  });

  const { mutate: createNoticeMutation, isLoading } = useMutation({
    mutationFn: createNotice,
    onSuccess: (data) => {
      onSuccess(data.id);
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Failed to create notice");
    },
  });

  const handleCreateNotice = useCallback(
    methods.handleSubmit((data) => {
      const payload: NoticeFormPayload = {
        name: data.name,
        version: data.version,
      };

      createNoticeMutation(payload);
    }),
    [methods, createNoticeMutation]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="small">
        <Dialog.Close className=" absolute top-4 right-4">
          <Button variant="soft" color="gray">
            Cancel
          </Button>
        </Dialog.Close>

        <FormProvider {...methods}>
          <Flex direction={"column"} gap={"2"} className=" p-4">
            <NoticeForm />

            <Flex gap="3" mt="4" justify="end">
              <Button
                variant="soft"
                color="gray"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateNotice}
                disabled={isLoading}
                highContrast
              >
                Create
              </Button>
            </Flex>
          </Flex>
        </FormProvider>
      </Dialog.Content>
    </Dialog>
  );
}
